// Quest rewards are always denominated in SKR (9 decimals). These helpers are
// the single source of truth for converting between the SKR amounts shown in
// the admin UI and the integer base-unit strings the quest APIs speak. The
// wire/API contracts still carry tokenSymbol/tokenDecimals/amountBaseUnits;
// the UI just never asks an admin to think in anything but SKR.
export const SKR_SYMBOL = 'SKR';
export const SKR_DECIMALS = 9;

const skrUnitsPattern = /^(?:(\d+)(?:\.(\d*))?|\.(\d+))$/;

/**
 * Formats an integer base-unit string (e.g. "1500000000") into a human-readable
 * SKR amount (e.g. "1.5"), trimming trailing zeros. Pure string math to avoid
 * float/BigInt precision issues. Falls back to the raw input when it is not a
 * non-negative integer.
 */
export function formatSkrBaseUnits(baseUnits: string): string {
  const trimmed = baseUnits.trim();
  if (!/^\d+$/.test(trimmed)) {
    return baseUnits;
  }

  const padded = trimmed.padStart(SKR_DECIMALS + 1, '0');
  const whole = padded.slice(0, padded.length - SKR_DECIMALS);
  const fraction = padded
    .slice(padded.length - SKR_DECIMALS)
    .replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : whole;
}

/**
 * Parses a human SKR amount (e.g. "1.5") into an integer base-unit string.
 * Returns "" for blank input and null when the amount is malformed or carries
 * more than 9 decimal places.
 */
export function parseSkrUnitsToBaseUnits(skrUnits: string): string | null {
  const trimmed = skrUnits.trim();
  if (!trimmed) {
    return '';
  }

  const match = skrUnitsPattern.exec(trimmed);
  if (!match) {
    return null;
  }

  const fraction = match[2] ?? match[3] ?? '';
  if (fraction.length > SKR_DECIMALS) {
    return null;
  }

  const whole = (match[1] ?? '0').replace(/^0+(?=\d)/, '');
  const baseUnits = `${whole}${fraction.padEnd(SKR_DECIMALS, '0')}`;
  return baseUnits.replace(/^0+(?=\d)/, '');
}

/** Formats base units as a labelled SKR amount for display, e.g. "1.5 SKR". */
export function formatSkrAmount(baseUnits: string): string {
  return `${formatSkrBaseUnits(baseUnits)} ${SKR_SYMBOL}`;
}
